/**
 * Оформление: подписи способов и причин отказа.
 *
 * Деньги считает сервер (`POST /checkout/quote`): и доставку, и потолок
 * списания, и кэшбэк. Здесь только слова — считать то же самое второй раз
 * значит завести второй источник правды про деньги.
 */
import type { Translate } from '../../ui/translate'
import type { DeliveryAddress, PayMethod, PickupStore, ShipMethod } from './api'

/** Названия платёжных систем не переводятся; остальное — `checkout:pay.*`. */
const BRANDS: Partial<Record<PayMethod, string>> = {
  click: 'Click',
  payme: 'Payme',
  uzum: 'Uzum',
  anorbank: 'Anorbank',
  oson: 'Oson',
  alif: 'Alif',
  xazna: 'Xazna',
  beepul: 'Beepul',
  trastpay: 'TrastPay',
}

const TRANSLATED: PayMethod[] = ['card', 'saved_card', 'pay_at_pharmacy']

export function payLabel(code: PayMethod, t: Translate): string {
  if (TRANSLATED.includes(code)) return t(`checkout:pay.${code}`)
  return BRANDS[code] ?? code
}

/** Почему способ недоступен. Ветвимся по коду, а не по тексту сервера. */
const REASONS = [
  'delivery_unavailable',
  'address_required',
  'rx_requires_pickup',
  'no_pharmacy_available',
  'out_of_stock',
  'upstream_failed',
  'no_saved_card',
]

export function reasonText(reason: string, t: Translate): string {
  return t(`checkout:reason.${REASONS.includes(reason) ? reason : 'other'}`)
}

/**
 * Сохранённые адреса, по которым можно везти: без точки по адресу не найти
 * ни аптеку, ни курьера. Основной (`is_default`) — первым.
 */
export function locatedAddresses(
  addresses: {
    id: number
    tag: string
    label: string
    lat: number | null
    lng: number | null
    def: boolean
  }[],
): DeliveryAddress[] {
  return [...addresses]
    .sort((a, b) => Number(b.def) - Number(a.def))
    .flatMap(({ id, tag, label, lat, lng }) =>
      lat !== null && lng !== null ? [{ id, tag, label, lat, lng }] : [],
    )
}

/** Как назвать адрес на кнопке: ярлык, если он есть, иначе сама улица. */
export function addressTitle(address: DeliveryAddress): string {
  return address.tag || address.label
}

/** Чем адрес отличается от других: сохранённый — номером, новый — точкой. */
export function addressKey(address: DeliveryAddress): string {
  return address.id ? `id:${address.id}` : `${address.lat},${address.lng}`
}

/**
 * Сервер не посчитал доставку по указанному адресу. Без адреса тот же ответ
 * значит, что курьерская служба выключена; с адресом — что курьер туда не
 * едет или не назвал цену (В-14), и адрес надо указать другой.
 */
export function addressRejected(
  address: DeliveryAddress | null,
  delivery: { available: boolean; reason: string },
): boolean {
  return address !== null && !delivery.available && delivery.reason === 'delivery_unavailable'
}

/**
 * Доставку можно выбрать всегда, кроме рецептурной корзины: рецепт отдают
 * только в аптеке (R-11). Остальные отказы сервера (служба выключена, некому
 * собрать) способ не прячут — пока он недоступен, под ним причина, а заказ с
 * доставкой не уходит: без расчёта сервера кнопка заказа выключена.
 */
export function deliverySelectable(
  delivery: { available: boolean; reason: string },
  hasRx = false,
): boolean {
  return !hasRx && delivery.reason !== 'rx_requires_pickup'
}

/**
 * Аптеки самовывоза — только те, что соберут весь заказ (`can_fulfil`): в
 * остальную покупатель пришёл бы за неполным заказом, выбирать её незачем.
 */
export function fulfilling<T extends { canFulfil: boolean }>(pickup: T[]): T[] {
  return pickup.filter((store) => store.canFulfil)
}

/**
 * Способы оплаты группами: сохранённые карты, онлайн (приложения) и при
 * получении. Карта на странице банка (`card`) отдельной строкой не идёт:
 * картой платят из «Моих карт». Незнакомый сервер-код — тоже онлайн: оплатить
 * «на месте» можно только тем, что аптека точно принимает. При доставке
 * платят только онлайн — курьер деньги не берёт (R-29).
 */
export function payGroups<T extends { code: PayMethod }>(methods: T[], ship: ShipMethod) {
  return {
    saved: methods.find((m) => m.code === 'saved_card'),
    online: methods.filter(
      (m) => m.code !== 'pay_at_pharmacy' && m.code !== 'saved_card' && m.code !== 'card',
    ),
    onReceipt: ship === 'delivery' ? [] : methods.filter((m) => m.code === 'pay_at_pharmacy'),
  }
}

/**
 * Какой способ отметить: прежний, пока он на экране и доступен; иначе первый
 * доступный из онлайн и «при получении». Сохранённую карту сами не выбираем —
 * какой платить, решает покупатель; но если больше платить нечем, отмечаем
 * её группу, и заказ ждёт, пока он выберет или добавит карту.
 */
export function pickPay(
  current: PayMethod,
  methods: { code: PayMethod; available: boolean }[],
  ship: ShipMethod,
): PayMethod {
  const { saved, online, onReceipt } = payGroups(methods, ship)
  const offered = [...online, ...onReceipt].filter((m) => m.available).map((m) => m.code)
  if (offered.includes(current) || (current === 'saved_card' && saved)) return current
  return offered.at(0) ?? (saved ? 'saved_card' : current)
}

/**
 * С чего начать оплату. Был заказ — тем же способом, что и прошлый (картой —
 * той же картой, если она ещё есть). Заказа не было или тот способ сейчас
 * недоступен — сохранённой картой, когда карты есть. Иначе null: решит
 * `pickPay` по списку способов.
 */
export function preferredPay(
  lastMethod: string | undefined,
  lastCard: number,
  cards: { id: number }[],
  methods: { code: PayMethod; available: boolean }[],
): { pay: PayMethod; cardId: number } | null {
  const card = cards.find((item) => item.id === lastCard) ?? cards.at(0)
  const byCard =
    card && methods.some((m) => m.code === 'saved_card' && m.available)
      ? { pay: 'saved_card' as const, cardId: card.id }
      : null
  if (lastMethod === 'saved_card') return byCard
  const last = methods.find((m) => m.code === lastMethod && m.available)
  return last ? { pay: last.code, cardId: 0 } : byCard
}

/**
 * Сколько баллов просить у сервера. При включении в поле сразу стоит потолок
 * из расчёта; стёрли поле — не списываем ничего. Больше потолка не просим,
 * сервер всё равно срежет (R-23…R-25).
 */
export function spendAmount(on: boolean, text: string, max: number): number {
  if (!on) return 0
  return Math.min(Number(text), max)
}

/** В поле баллов попадают только цифры, без ведущих нулей. */
export function spendDigits(input: string): string {
  return input.replace(/\D/g, '').replace(/^0+/, '')
}

/** Карты, которые привязываются (Uzcard, Humo), — ровно 16 цифр. */
export const CARD_LENGTH = 16

/** Номер карты — только цифры, лишние сверх 16 не печатаются. */
export function cardDigits(input: string): string {
  return input.replace(/\D/g, '').slice(0, CARD_LENGTH)
}

/** Как номер печатают на карте: группы по четыре. */
export function formatCardNumber(digits: string): string {
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ')
}

/** Срок действия как на карте: ММ/ГГ; косая черта встаёт сама. */
export function formatExpire(input: string): string {
  const digits = input.replace(/\D/g, '').slice(0, 4)
  return digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits
}

/** Можно отправлять в банк: все 16 цифр и настоящий месяц. */
export function cardReady(number: string, expire: string): boolean {
  const month = Number(expire.slice(0, 2))
  return number.length === CARD_LENGTH && /^\d{2}\/\d{2}$/.test(expire) && month >= 1 && month <= 12
}

/** Маска 860012******9999 → «•• 9999»: на экране хватает последних цифр. */
export function cardTail(mask: string): string {
  return `•• ${mask.slice(-4)}`
}

/** Коды ошибок привязки, у которых есть свой текст (`checkout:cards.errors.*`). */
const CARD_ERRORS = ['card_limit', 'card_rejected', 'otp_invalid', 'otp_expired', 'upstream_failed']

/** Что сказать, если банк или сервер не приняли карту; `''` — общий текст. */
export function cardErrorKey(code: string): string {
  return CARD_ERRORS.includes(code) ? `checkout:cards.errors.${code}` : ''
}

/**
 * Какая аптека выбрана: та, что выбрал покупатель, пока она собирает заказ;
 * иначе первая из тех, что собирают, — сервер ставит ближние вперёд. 0 —
 * собрать некому.
 */
export function pickupStore(pickup: PickupStore[], picked: number): number {
  const ok = fulfilling(pickup)
  if (ok.some((store) => store.storeId === picked)) return picked
  return ok.at(0)?.storeId ?? 0
}

/**
 * Аккаунт рецензента магазина приложений (R-02a): ему на оформлении только
 * оплата в аптеке — без онлайн-способов и карт. Сравниваем цифры: номер в
 * профиле и в настройке может быть записан по-разному.
 */
export function reviewPayMethods<T extends { code: PayMethod }>(
  methods: T[],
  phone: string | undefined,
  reviewPhone: string,
): T[] {
  const digits = (phone ?? '').replace(/\D/g, '')
  if (!reviewPhone || digits !== reviewPhone) return methods
  return methods.filter((m) => m.code === 'pay_at_pharmacy')
}
